import {
  // 8 Exact Homepage Icons (DO NOT REPLACE)
  Building2,
  ShieldCheck,
  Droplets,
  Recycle,
  Trees,
  BookOpen,
  Hammer,
  HeartPulse,

  // Selected Vector Icons for Remaining Sectors
  Activity,
  Trophy,
  MonitorSmartphone,
  Radio,
  BriefcaseBusiness,
  Tractor,
  Siren,
  Palette,
  Scale,
  HandHeart,
  UsersRound,
  Globe2,
  Landmark,

  // Fallback vector icon
  Layers,
} from 'lucide-react';

/**
 * Reusable icon map keyed by icon name string
 */
export const iconComponents = {
  // 8 Exact Homepage Icons
  Building2,
  ShieldCheck,
  Droplets,
  Recycle,
  Trees,
  BookOpen,
  Hammer,
  HeartPulse,

  // Remaining Sector Icons
  Activity,
  Trophy,
  MonitorSmartphone,
  Radio,
  BriefcaseBusiness,
  Tractor,
  Siren,
  Palette,
  Scale,
  HandHeart,
  UsersRound,
  Globe2,
  Landmark,

  // Safe fallback
  Layers,
};

/**
 * Slug to Icon Component Mapping
 */
const slugToIconMap = {
  // 8 Homepage Sectors
  'urban-planning': Building2,
  'security': ShieldCheck,
  'drinking-water': Droplets,
  'waste-management': Recycle,
  'forest-tourism': Trees,
  'education': BookOpen,
  'skills-entrepreneurship': Hammer,
  'health': HeartPulse,

  // Remaining Sectors
  'healthy-lifestyle': Activity,
  'sports': Trophy,
  'smart-city': MonitorSmartphone,
  'media-communication': Radio,
  'trade-industry': BriefcaseBusiness,
  'agriculture': Tractor,
  'disaster-management': Siren,
  'culture-identity': Palette,
  'governance': Scale,
  'social-security': HandHeart,
  'women-youth': UsersRound,
  'international-relations': Globe2,
  'good-governance': Landmark,
};

/**
 * Order Number (1-21) to Icon Component Mapping
 */
const orderToIconMap = {
  1: Building2,
  2: ShieldCheck,
  3: Droplets,
  4: Recycle,
  5: Trees,
  6: BookOpen,
  7: Hammer,
  8: HeartPulse,
  9: Activity,
  10: Trophy,
  11: MonitorSmartphone,
  12: Radio,
  13: BriefcaseBusiness,
  14: Tractor,
  15: Siren,
  16: Palette,
  17: Scale,
  18: HandHeart,
  19: UsersRound,
  20: Globe2,
  21: Landmark,
};

/**
 * Keyword-based Title Mapping
 */
function getIconByKeyword(title = '') {
  const str = String(title).toLowerCase();

  // 8 Homepage sectors
  if (str.includes('शहरी') || str.includes('पूर्वाधार') || str.includes('urban') || str.includes('infrastructure')) return Building2;
  if (str.includes('सुरक्षा') || str.includes('security')) return ShieldCheck;
  if (str.includes('खानेपानी') || str.includes('पानी') || str.includes('drinking') || str.includes('water')) return Droplets;
  if (str.includes('फोहोर') || str.includes('प्रदूषण') || str.includes('waste') || str.includes('pollution')) return Recycle;
  if (str.includes('वन') || str.includes('वातावरण') || str.includes('पर्यटन') || str.includes('forest') || str.includes('tourism')) return Trees;
  if (str.includes('शिक्षा') || str.includes('अनुसन्धान') || str.includes('ज्ञान') || str.includes('education') || str.includes('knowledge')) return BookOpen;
  if (str.includes('शिल्प') || str.includes('सीप') || str.includes('उद्यमशीलता') || str.includes('skills') || str.includes('craft')) return Hammer;
  if (str.includes('स्वास्थ्य') || str.includes('जनस्वास्थ्य') || str.includes('health')) return HeartPulse;

  // Remaining sectors
  if (str.includes('जीवनशैली') || str.includes('उपभोक्ता') || str.includes('lifestyle') || str.includes('wellness')) return Activity;
  if (str.includes('खेलकुद') || str.includes('खेल') || str.includes('sport') || str.includes('athletic')) return Trophy;
  if (str.includes('सूचना प्रविधि') || str.includes('स्मार्ट') || str.includes('smart') || str.includes('technology') || str.includes(' it')) return MonitorSmartphone;
  if (str.includes('मिडिया') || str.includes('सञ्चार') || str.includes('media') || str.includes('communication')) return Radio;
  if (str.includes('व्यापार') || str.includes('उद्योग') || str.includes('आर्थिक') || str.includes('trade') || str.includes('industry') || str.includes('economic')) return BriefcaseBusiness;
  if (str.includes('कृषि') || str.includes('सहकारी') || str.includes('agriculture') || str.includes('cooperative') || str.includes('farm')) return Tractor;
  if (str.includes('विपद्') || str.includes('विपद') || str.includes('disaster') || str.includes('emergency')) return Siren;
  if (str.includes('कला') || str.includes('संस्कृति') || str.includes('पहिचान') || str.includes('art') || str.includes('culture') || str.includes('identity')) return Palette;
  if (str.includes('नीति') || str.includes('वित्तीय') || str.includes('governance') || str.includes('policy') || str.includes('finance')) return Scale;
  if (str.includes('सामाजिक सुरक्षा') || str.includes('समावेशी') || str.includes('social') || str.includes('inclusive')) return HandHeart;
  if (str.includes('महिला') || str.includes('बालबालिका') || str.includes('युवा') || str.includes('women') || str.includes('youth') || str.includes('children')) return UsersRound;
  if (str.includes('अन्तर्राष्ट्रिय') || str.includes('international')) return Globe2;
  if (str.includes('नगर शासन') || str.includes('सार्वजनिक सेवा') || str.includes('सुशासन') || str.includes('public service') || str.includes('civic')) return Landmark;

  return null;
}

/**
 * Resolves a vector Lucide Icon component for any given sector object or identifier.
 * Priority:
 * 1. Matching Lucide component name if provided (and not a single-char emoji)
 * 2. Slug match
 * 3. Order index match
 * 4. Title / Nepali Name keyword match
 * 5. Safe vector fallback: Building2
 *
 * Guaranteed: NEVER returns lightning/bolt icon and NEVER returns emojis.
 */
export function getSectorIcon(sector) {
  if (!sector) return Building2;

  // If sector is directly a string icon name (e.g. 'Building2', 'ShieldCheck')
  if (typeof sector === 'string') {
    if (iconComponents[sector]) return iconComponents[sector];
    if (slugToIconMap[sector]) return slugToIconMap[sector];
    const kwIcon = getIconByKeyword(sector);
    if (kwIcon) return kwIcon;
    return Building2;
  }

  // 1. Check if sector.icon is a valid named Lucide icon
  if (typeof sector.icon === 'string' && sector.icon.length > 2 && iconComponents[sector.icon]) {
    return iconComponents[sector.icon];
  }

  // 2. Check slug match
  const slug = sector.slug || sector.nepaliSlug;
  if (slug && slugToIconMap[slug]) {
    return slugToIconMap[slug];
  }

  // 3. Check order index match
  const orderNum = Number(sector.order ?? sector.id);
  if (orderNum && orderToIconMap[orderNum]) {
    return orderToIconMap[orderNum];
  }

  // 4. Keyword matching against Nepali/English titles and names
  const titles = [sector.title, sector.nepaliTitle, sector.name].filter(Boolean).join(' ');
  const kwIcon = getIconByKeyword(titles);
  if (kwIcon) {
    return kwIcon;
  }

  // 5. Default fallback
  return Building2;
}

export default getSectorIcon;
